/**
 * Legacy-origin guard for the installed PWA.
 *
 * The app used to be served from clinikchat-web.fly.dev. That origin is
 * cross-site to the API under the public-suffix rules for *.fly.dev, so the
 * SameSite=None refresh cookie is third-party there and Safari / Brave /
 * incognito drop it: every reload becomes 400 MISSING_TOKEN → logout. nginx
 * now 301s the old host, but an installed PWA never reaches nginx — its
 * service worker answers navigations straight from the precache. This
 * module runs before React mounts: if we are on a *.fly.dev host and a
 * different canonical origin is configured, unregister every service worker,
 * drop every cache and hop to the same path on the canonical origin.
 */

export interface LocationLike {
  hostname: string;
  origin: string;
  pathname: string;
  search: string;
  hash: string;
}

export const LEGACY_HOST_SUFFIX = '.fly.dev';

/**
 * Pure decision: the URL to move to, or null when no migration is needed
 * (not a legacy host, no canonical URL configured, or already there).
 */
export function getLegacyOriginRedirect(
  location: LocationLike,
  canonicalAppUrl: string | undefined,
): string | null {
  if (!canonicalAppUrl) return null;
  if (!location.hostname.toLowerCase().endsWith(LEGACY_HOST_SUFFIX)) return null;
  let canonicalOrigin: string;
  try {
    canonicalOrigin = new URL(canonicalAppUrl).origin;
  } catch {
    return null;
  }
  if (canonicalOrigin === location.origin) return null;
  return `${canonicalOrigin}${location.pathname}${location.search}${location.hash}`;
}

export interface MigrationDeps {
  serviceWorker?: Pick<ServiceWorkerContainer, 'getRegistrations'> | null;
  caches?: Pick<CacheStorage, 'keys' | 'delete'> | null;
  replace: (url: string) => void;
}

/**
 * Tear down the legacy origin's offline state, then navigate. Each step is
 * best-effort: a failure to unregister or clear must never keep the user on
 * the broken origin, so we always reach `replace`.
 */
export async function migrateToCanonicalOrigin(target: string, deps: MigrationDeps): Promise<void> {
  try {
    const registrations = (await deps.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(registrations.map((r) => r.unregister().catch(() => false)));
  } catch {
    // Unsupported or blocked — carry on to the redirect.
  }
  try {
    const names = (await deps.caches?.keys()) ?? [];
    await Promise.all(names.map((name) => deps.caches!.delete(name).catch(() => false)));
  } catch {
    // Same: never block the redirect on cache cleanup.
  }
  deps.replace(target);
}

/**
 * Browser entry point used by main.tsx. Returns true when a redirect was
 * started (the caller should skip mounting the app).
 */
export function migrateOffLegacyOriginIfNeeded(
  canonicalAppUrl: string | undefined = import.meta.env.VITE_PUBLIC_APP_URL,
): boolean {
  if (typeof window === 'undefined') return false;
  const target = getLegacyOriginRedirect(window.location, canonicalAppUrl);
  if (!target) return false;
  void migrateToCanonicalOrigin(target, {
    serviceWorker: 'serviceWorker' in navigator ? navigator.serviceWorker : null,
    caches: 'caches' in window ? window.caches : null,
    replace: (url) => window.location.replace(url),
  });
  return true;
}
